import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";
import CONSTANTS from "shared/constants";
import strings from "resources/strings";
import {
  Typography,
  Flex,
  Space,
  Button,
  Radio,
  Tooltip,
  Card,
  message,
} from "antd";
const { Title, Text } = Typography;

import { CrownFilled, Loading3QuartersOutlined } from "@ant-design/icons";

import ApiManager from "resources/ApiManager/api";

import "./style.scss";
const styleWrapperClass = "alerts";

export default function Alerts() {
  const { isSmallView } = useWindowDimensions();

  const navigate = useNavigate();

  const user = useStore((state) => state.user);
  const updateUserRfc = useStore((state) => state.updateUserRfc);
  const activeRfc = useStore((state) => state.activeRfc);

  const active_rfc_obj = user.rfcs[activeRfc];

  const BannerFeatureNotAvailable = () => (
    <Space
      style={{
        marginTop: "20px",
        marginBottom: "20px",
        width: "100%",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Space direction="vertical">
        <Title level={3}>
          Disponible en el plan <CrownFilled className="fg-gold" /> Pro ó Básico
        </Title>
        <Title level={5}>
          Para configurar alertas de CFDIs nuevos o cancelados, deberás
          subscribirte al plan <CrownFilled className="fg-gold" /> Pro ó Básico.
        </Title>
        <Button onClick={() => navigate("/plan")}>Ver Planes</Button>
      </Space>
    </Space>
  );

  const AlertsConfig = () => {
    const [refreshSchedule, setRefreshSchedule] = useState(null);
    const [isRefreshScheduleSaving, setIsRefreshScheduleSaving] =
      useState(false);

    useEffect(() => {
      if (active_rfc_obj.refresh_schedule.startsWith("weekly")) {
        setRefreshSchedule("weekly");
      } else if (active_rfc_obj.refresh_schedule.startsWith("monthly")) {
        setRefreshSchedule("monthly");
      } else {
        setRefreshSchedule(active_rfc_obj.refresh_schedule);
      }
    }, []);

    const radioOptionStyle = isSmallView ? { borderRadius: "0px" } : null;
    const radioGroupStyle = isSmallView
      ? {
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }
      : null;

    const refreshScheduleOptions = [
      {
        label: isSmallView ? (
          <Flex justify="space-between">
            Manual
            {isRefreshScheduleSaving && (
              <Loading3QuartersOutlined
                spin={true}
                style={{ color: "white" }}
              />
            )}
          </Flex>
        ) : (
          "Manual"
        ),
        value: "manual",
        style: radioOptionStyle,
      },
      {
        label: isSmallView ? (
          <Flex justify="space-between">
            Mensual
            {isRefreshScheduleSaving && (
              <Loading3QuartersOutlined
                spin={true}
                style={{ color: "white" }}
              />
            )}
          </Flex>
        ) : (
          "Mensual"
        ),
        value: "monthly",
        style: radioOptionStyle,
      },
      {
        label: isSmallView ? (
          <Flex justify="space-between">
            Semanal
            {isRefreshScheduleSaving && (
              <Loading3QuartersOutlined
                spin={true}
                style={{ color: "white" }}
              />
            )}
          </Flex>
        ) : (
          "Semanal"
        ),
        value: "weekly",
        style: radioOptionStyle,
      },
      {
        label:
          active_rfc_obj.plan_name === "pro" ? (
            isSmallView ? (
              <Flex justify="space-between">
                Diario
                {isRefreshScheduleSaving && (
                  <Loading3QuartersOutlined
                    spin={true}
                    style={{ color: "white" }}
                  />
                )}
              </Flex>
            ) : (
              "Diario"
            )
          ) : (
            <Tooltip
              title={
                <Text style={{ color: "white" }}>
                  Las actualizaciones diarias están disponibles en el plan&nbsp;
                  <CrownFilled className="fg-gold" /> Pro.
                </Text>
              }
              placement="bottomLeft"
              overlayStyle={{ maxWidth: "500px" }}
            >
              <Space>
                <CrownFilled className="fg-gold" /> Diario
              </Space>
            </Tooltip>
          ),
        value: "daily",
        style: radioOptionStyle,
        disabled: active_rfc_obj.plan_name === "pro" ? false : true,
      },
    ];

    const refreshScheduleOnChange = async ({ target: { value } }) => {
      setRefreshSchedule(value);
      setIsRefreshScheduleSaving(true);

      //Update on server
      const apiRes = await ApiManager.postUserRfcRefreshSchedule(
        active_rfc_obj.rfc,
        value
      );

      setIsRefreshScheduleSaving(false);

      //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
      if (
        apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED
      ) {
        return;
      }

      if (apiRes.response.code === -1 || apiRes.response.code !== 2000) {
        message.error(strings.alertUnexpectedError);
        return;
      }

      const updatedRfc = apiRes.data.rfcs[0];
      updateUserRfc(updatedRfc);
    };

    const radioGroup = (
      <Radio.Group
        options={refreshScheduleOptions}
        onChange={refreshScheduleOnChange}
        value={refreshSchedule}
        optionType="button"
        buttonStyle="solid"
        disabled={active_rfc_obj.refresh_status === "inactive"}
        style={radioGroupStyle}
        size={isSmallView ? "large" : "medium"}
      />
    );

    const radioGroupContainer =
      active_rfc_obj.refresh_status === "inactive" ? (
        <Tooltip
          title="No es posible actualizar la frecuencia de actualizaciones mientras tu RFC esté inactivo."
          overlayStyle={{ maxWidth: "300px" }}
        >
          {radioGroup}
        </Tooltip>
      ) : (
        radioGroup
      );

    return (
      <>
        <Title level={4}>Configura las alertas según tus preferencias.</Title>
        <Flex style={{ marginTop: "30px" }} vertical gap={30}>
          <Card>
            <Space direction="vertical" size={10}>
              <Text>
                Selecciona la frecuencia con la que se actualizarán tus CFDIs.
                Recibirás una alerta por correo electrónico solamente si existen
                CFDIs nuevos desde la última actualización.
              </Text>

              {isSmallView ? (
                radioGroupContainer
              ) : (
                <Space size={15}>
                  {radioGroupContainer}

                  {isRefreshScheduleSaving && (
                    <Loading3QuartersOutlined
                      spin={true}
                      style={{ color: "gray" }}
                    />
                  )}
                </Space>
              )}
            </Space>
          </Card>
        </Flex>
      </>
    );
  };

  return (
    <div className={styleWrapperClass}>
      <Title level={1}>Alertas</Title>
      {active_rfc_obj.plan_name === "free" ? (
        <BannerFeatureNotAvailable />
      ) : (
        <AlertsConfig />
      )}
    </div>
  );
}
